// Inspection presentation meta — the priority/state chip maps and the
// org-structure line derivation, shared by the table column pack
// (InspectionColumns) and the cards view (InspectionCards) so the two surfaces
// can't drift. The avatar and date helpers they also need are the shared ones
// in utils/.

import type { TFunction } from 'i18next';
import type {
  InspectionListItem,
  InspectionPriority,
  InspectionState,
} from '@/api/endpoints/inspectionApi';

export const PRIORITY_META: Record<
  InspectionPriority,
  { chip: 'info' | 'warning' | 'danger'; icon: string }
> = {
  low: { chip: 'info', icon: 'chevron-down' },
  medium: { chip: 'warning', icon: 'equal' },
  high: { chip: 'danger', icon: 'chevrons-up' },
};

export const STATE_META: Record<
  InspectionState,
  { chip: 'success' | 'neutral' | 'warning'; icon: string }
> = {
  active: { chip: 'success', icon: 'circle-check' },
  draft: { chip: 'neutral', icon: 'circle-dashed' },
  suspended: { chip: 'warning', icon: 'pause' },
};

// Deterministic per-person avatar tone so the same person is always one color.

/** The company-structure cell's two lines: the first names the scope — the
 *  selected node, or the "every node in this level" text when none is — and
 *  the second is always the level name. Null when the inspection has no level
 *  at all (callers render a dash or skip the block). */
export const structureLines = (
  i: InspectionListItem,
  t: TFunction,
): { icon: string; name: string; sub: string } | null => {
  if (!i.companyLevel) return null;
  const node = i.companyNode;
  return {
    icon: i.companyLevel.icon || 'layers',
    name: node ? node.name : t('inspection_structure_every_node'),
    sub: i.companyLevel.name,
  };
};
